import type {
  WorkspaceDraftDocumentModel as WorkspaceDraftDocument,
  WorkspaceQuickStartKind,
} from '@heidi/agents';
import {
  voiceWorkspaceMessages as messages,
  sampleOverviewCard,
  useVoiceWorkspace,
  useWorkspaceStreamer,
} from '@heidi/agents';
import { voiceBuildMode } from '@heidi/agents/data';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { voiceController } from '../controller';
import type { VoiceConnectionStatus } from '../store/useVoiceAssistantStore';

const QUICK_START_REQUESTS: Record<
  WorkspaceQuickStartKind,
  (typeof messages)[keyof typeof messages]
> = {
  'start-session': messages.quickRequestStartSession,
  brainstorm: messages.quickRequestBrainstorm,
  'dictate-notes': messages.quickRequestDictateNotes,
  'capture-work': messages.quickRequestCaptureWork,
};

/** Re-enable the quick-start chips if the agent never fills the canvas. */
const QUICK_START_PENDING_TIMEOUT_MS = 15_000;

/**
 * The overlay's canvas-level user actions, shared between the empty-state
 * quick starts, the draft document, and the info feed:
 *
 * - **Create** — the human-in-the-loop commit of a draft. Live path sends the
 *   commit over the voice socket (server answers with `workspace_update`
 *   frames); without a session the demo path walks the status locally.
 * - **Quick start** — once the socket is active, chips are live even during
 *   the canned hello (text barge-in). While connecting they stay busy; a
 *   programmatic seed still parks on ``init.text``. Idle/error demo path
 *   streams a sample draft / appends a sample card.
 * - **Follow up** — conversational drill-down from an info card; the agent
 *   answers with the next card appended to the feed.
 */
export function useWorkspaceCanvasActions({
  document,
  hasCanvasContent,
  connection,
  sendText,
}: {
  document: WorkspaceDraftDocument | null;
  /** True when the canvas is already filled (draft or info feed). */
  hasCanvasContent: boolean;
  connection: VoiceConnectionStatus;
  sendText: (text: string) => void;
}) {
  const intl = useIntl();
  const { setStatus, setCreateError, presentInfoCard } = useVoiceWorkspace();
  const streamer = useWorkspaceStreamer();
  // Mirrors the controller's dev heuristic: dev builds keep the demo paths
  // (quick-starts, follow-ups) usable without a live voice session.
  const isDevBuild = voiceBuildMode().isDevBuild;

  const isActive = connection === 'active';
  const [quickStartPending, setQuickStartPending] = useState(false);
  const createTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFollowUp = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (createTimer.current) clearTimeout(createTimer.current);
    },
    []
  );

  // Flush follow-ups queued while connecting; drop them if connect fails/aborts.
  // Quick-starts seed ``init`` via seedOpen — they must not flush as TEXT.
  useEffect(() => {
    if (isActive) {
      const followUp = pendingFollowUp.current;
      if (followUp) {
        pendingFollowUp.current = null;
        sendText(followUp);
      }
      return;
    }
    if (connection === 'connecting') return;
    pendingFollowUp.current = null;
  }, [isActive, connection, sendText]);

  // Quick-start pending ends when the agent fills the canvas, or times out so
  // the chips never stay dead after an agent answer that isn't a draft/card.
  useEffect(() => {
    if (!quickStartPending) return;
    if (hasCanvasContent) {
      setQuickStartPending(false);
      return;
    }
    const timeout = setTimeout(
      () => setQuickStartPending(false),
      QUICK_START_PENDING_TIMEOUT_MS
    );
    return () => clearTimeout(timeout);
  }, [quickStartPending, hasCanvasContent]);

  const handleCreate = useCallback(() => {
    if (!document) return;
    if (isActive) {
      const sessionId = document.sessionId ?? getCurrentSessionId();
      if (!sessionId) {
        setCreateError('no_session');
        return;
      }
      setCreateError(null);
      setStatus('creating');
      voiceController.createWorkspaceDocument({
        sessionId,
        title: document.title,
        content: document.content,
      });
      return;
    }
    setStatus('creating');
    createTimer.current = setTimeout(() => setStatus('created'), 600);
  }, [document, isActive, setCreateError, setStatus]);

  const handleQuickStart = useCallback(
    (kind: WorkspaceQuickStartKind) => {
      const request = intl.formatMessage(QUICK_START_REQUESTS[kind]);
      // Active session: hello is interruptible — TEXT cuts her off (BE + local flush).
      if (isActive) {
        setQuickStartPending(true);
        sendText(request);
        return;
      }
      // Connecting: chips are busy in the UI; if called anyway, seed init.text.
      if (connection === 'connecting') {
        setQuickStartPending(true);
        voiceController.seedOpen(request);
        return;
      }
      // Demo fallbacks only when there is no live session in flight (idle/error).
      if (kind === 'brainstorm' || kind === 'start-session') {
        presentInfoCard(sampleOverviewCard());
        return;
      }
      if (kind === 'dictate-notes') {
        void streamer.stream('session-document');
        return;
      }
      void streamer.stream('referral-letter');
    },
    [isActive, connection, sendText, streamer, intl, presentInfoCard]
  );

  const handleFollowUp = useCallback(
    (text: string) => {
      if (isActive) {
        sendText(text);
        return;
      }
      if (connection === 'connecting') {
        pendingFollowUp.current = text;
        return;
      }
      presentInfoCard(sampleOverviewCard());
    },
    [isActive, connection, sendText, presentInfoCard]
  );

  return {
    // Connecting = plumbing only — chips wait until active. Greeting does not lock them.
    quickStartBusy:
      streamer.streaming || quickStartPending || connection === 'connecting',
    followUpsEnabled: isActive || connection === 'connecting' || isDevBuild,
    handleCreate,
    handleQuickStart,
    handleFollowUp,
  };
}
